<style scoped>
.zoezi-image-text__text >>> a {
    pointer-events: auto;
    cursor: pointer;
    color: inherit;
}
.zoezi-image-text__text >>> ul {
    list-style-type: disc;
    padding-left: 1.5em;
    margin: 0.25em 0;
}
.zoezi-image-text__text >>> ol {
    list-style-type: decimal;
    padding-left: 1.5em;
    margin: 0.25em 0;
}
.zoezi-image-text__text >>> li {
    display: list-item;
}
.zoezi-image-text__text >>> h1,
.zoezi-image-text__text >>> h2,
.zoezi-image-text__text >>> h3 {
    font-family: inherit;
}
.zoezi-image-text__text >>> p {
    margin: 0;
}
.zoezi-image-text__text >>> p + p {
    margin-top: 0.5em;
}
.zoezi-image-text__text >>> p:empty {
    display: none;
}


</style>

<template>
    <div class="zoezi-image-text" :style="containerStyle">
        <div class="zoezi-image-text__text" :style="textStyle">
            <div v-safe-html="html" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'zoezi-image-text',

    zoezi: {
        title: 'Image with text',
        icon: 'mdi-image-text',
        getDefaultStyle: () => ({})
    },

    props: {
        backgroundImage: {
            title: 'Background image',
            type: String,
            fulltype: 'image'
        },
        backgroundPosition: {
            title: 'Background position',
            type: String,
            fulltype: 'backgroundposition',
            default: '50% 50%',
            typeinfo: { sliders: true }
        },
        height: {
            title: 'Height',
            type: String,
            fulltype: 'csssize',
            default: '400px',
            typeinfo: { max: 2000 }
        },
        cover: {
            title: 'Cover with image',
            type: Boolean,
            default: true
        },
        html: {
            title: 'Text',
            type: String,
            fulltype: 'html',
            typeinfo: { inline: true }
        },
        textPosition: {
            title: 'Text position',
            type: String,
            fulltype: 'backgroundposition',
            default: '50% 50%',
            typeinfo: { sliders: true }
        },
        fontSize: {
            title: 'Text size (px)',
            type: Number,
            default: 16,
            typeinfo: {
                min: 8,
                max: 100
            }
        },
        fontFamily: {
            title: 'Font',
            type: String,
            fulltype: 'font'
        },
        textColor: {
            title: 'Text color',
            type: String,
            fulltype: 'color'
        },
        overlayColor: {
            title: 'Text background color',
            type: String,
            fulltype: 'color'
        },
        overlayOpacity: {
            title: 'Text background opacity (0-100)',
            type: Number,
            default: 50,
            typeinfo: {
                min: 0,
                max: 100
            }
        }
    },

    computed: {
        containerStyle() {
            const heightStr = this.height || '400px';
            const heightNum = parseFloat(heightStr);
            const heightUnit = heightStr.replace(/[\d.]+/, '').trim();
            // For px heights: scale fluidly with viewport width using clamp().
            // Reference width 1280px — at that viewport width the component
            // renders at exactly the configured px value, scaling proportionally
            // on narrower or wider screens.
            const computedHeight = (heightUnit === 'px' && !isNaN(heightNum))
                ? `clamp(${Math.round(heightNum * 0.55)}px, ${(heightNum / 1280 * 100).toFixed(2)}vw, ${heightNum}px)`
                : heightStr;
            const style = {
                position: 'relative',
                overflow: 'hidden',
                backgroundRepeat: 'no-repeat',
                height: computedHeight,
                backgroundPosition: this.backgroundPosition || '50% 50%',
                backgroundSize: this.cover !== false ? 'cover' : 'contain',
            };
            if (this.backgroundImage) {
                style.backgroundImage = `url(${this.$api.getImageUrl(this.backgroundImage)})`;
            }
            return style;
        },
        textStyle() {
            const clampedFs = Math.min(Math.max(this.fontSize || 16, 8), 100);
            // Font size scales fluidly with viewport width, min 40% of desktop value.
            const computedFs = `clamp(${Math.max(Math.round(clampedFs * 0.4), 8)}px, ${(clampedFs / 1280 * 100).toFixed(3)}vw, ${clampedFs}px)`;
            const style = {
                position: 'absolute',
                width: 'max-content',
                maxWidth: '80%',
                fontSize: computedFs,
                overflowWrap: 'break-word',
            };
            if (this.fontFamily) {
                style.fontFamily = this.fontFamily;
            }
            if (this.textColor) {
                style.color = this.$root.colorToHex(this.textColor);
            }
            if (this.overlayColor) {
                style.backgroundColor = this.$root.h2r(this.$root.colorToHex(this.overlayColor), (this.overlayOpacity ?? 50) / 100);
                style.padding = '8px 12px';
                style.borderRadius = '4px';
            }
            const { x, y } = this.parseTextPosition();
            // x: 0 = far left, 100 = far right.
            // y: 0 = far bottom, 100 = far top (inverted from CSS top).
            style.left = `${x}%`;
            style.top = `${100 - y}%`;
            style.transform = `translate(${-x}%, -50%)`;
            return style;
        }
    },

    methods: {
        parseTextPosition() {
            const v = this.textPosition || '50% 50%';
            if (v.includes('%')) {
                const parts = v.trim().split(/\s+/);
                const x = parseFloat(parts[0]);
                const y = parseFloat(parts[1]);
                return { x: isNaN(x) ? 50 : x, y: isNaN(y) ? 50 : y };
            }
            // Backwards compat: handle old "left top" / "center center" format
            const [h = 'center', vert = 'center'] = v.trim().split(/\s+/);
            const hMap = { left: 0, center: 50, right: 100 };
            const vMap = { top: 0, center: 50, bottom: 100 };
            return { x: hMap[h] ?? 50, y: vMap[vert] ?? 50 };
        },
    }
}
</script>
